/** TanStack Query `queryOptions` factories — the single place query keys are defined. */
import { api } from "@client/lib/api";
import type { SectorLabel } from "@shared/sectors";
import type { QueryClient } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";

export const authQuery = () =>
  queryOptions({
    queryKey: ["auth"] as const,
    queryFn: () => api.auth.me(),
  });

export const tokensQuery = () =>
  queryOptions({
    queryKey: ["tokens"] as const,
    queryFn: () => api.tokens.list(),
  });

export const candlesQuery = (id: string) =>
  queryOptions({
    queryKey: ["tokens", id, "candles"] as const,
    queryFn: () => api.tokens.candles(id),
  });

export const overviewQuery = () =>
  queryOptions({
    queryKey: ["market", "overview"] as const,
    queryFn: () => api.market.overview(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

export const topCoinsQuery = () =>
  queryOptions({
    queryKey: ["market", "top"] as const,
    queryFn: () => api.market.top(),
    staleTime: 60_000,
  });

export const searchQuery = (q: string) =>
  queryOptions({
    queryKey: ["market", "search", { q }] as const,
    queryFn: () => api.market.search(q),
    enabled: q.length >= 1,
    staleTime: 10 * 60_000,
  });

export const basketsQuery = () =>
  queryOptions({
    queryKey: ["baskets"] as const,
    queryFn: () => api.baskets.list(),
  });

export const basketQuery = (id: string) =>
  queryOptions({
    queryKey: ["baskets", id] as const,
    queryFn: () => api.baskets.get(id),
  });

export const journalQuery = (params: { basketId?: string; limit?: number } = {}) =>
  queryOptions({
    queryKey: ["journal", params] as const,
    queryFn: () => api.journal.list(params),
  });

export const alertsQuery = () =>
  queryOptions({
    queryKey: ["alerts"] as const,
    queryFn: () => api.alerts.list(),
  });

export const alertEventsQuery = () =>
  queryOptions({
    queryKey: ["alerts", "events"] as const,
    queryFn: () => api.alerts.events(),
  });

export const settingsQuery = () =>
  queryOptions({
    queryKey: ["settings"] as const,
    queryFn: () => api.settings.get(),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const syncQuery = () =>
  queryOptions({
    queryKey: ["sync"] as const,
    queryFn: () => api.sync.status(),
  });

export const sectorsQuery = () =>
  queryOptions({
    queryKey: ["sectors"] as const,
    queryFn: () => api.sectors.radar(),
    staleTime: 5 * 60_000,
  });

export const sectorQuery = (label: SectorLabel) =>
  queryOptions({
    queryKey: ["sectors", label] as const,
    queryFn: () => api.sectors.detail(label),
    staleTime: 5 * 60_000,
  });

export function invalidateAfterBasketChange(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ["baskets"] }),
    qc.invalidateQueries({ queryKey: ["tokens"] }),
    qc.invalidateQueries({ queryKey: ["alerts"] }),
    qc.invalidateQueries({ queryKey: ["journal"] }),
  ]).then(() => undefined);
}

export function invalidateAfterTokenChange(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ["tokens"] }),
    qc.invalidateQueries({ queryKey: ["market", "search"] }),
  ]).then(() => undefined);
}
